import axios from "axios";
import toast from "react-hot-toast";

const api = axios.create({ baseURL: "https://back.teknulugypos.com" });

api.interceptors.request.use((cfg) => {
	const token = localStorage.getItem("tkn_token");
	if (token) cfg.headers.Authorization = `Bearer ${token}`;
	return cfg;
});

api.interceptors.response.use(
	(res) => res,
	(err) => {
		if (err.response?.status === 401) {
			localStorage.removeItem("tkn_token");
			localStorage.removeItem("tkn_user");
			window.location.reload();
		}
		const msg =
			err.response?.data?.message || err.message || "Something went wrong";
		if (err.response?.status !== 401) toast.error(msg);
		return Promise.reject(err);
	},
);

export const authAPI = {
	login: (email: string, password: string) =>
		api.post("/auth/login", { email, password }),
	pinLogin: (userId: string, pin: string) =>
		api.post("/auth/pin-login", { userId, pin }),
	logout: () => api.post("/auth/logout"),
	me: () => api.get("/auth/me"),
};

export const usersAPI = {
	getAll: () => api.get("/users"),
	getOne: (id: string) => api.get(`/users/${id}`),
	create: (data: object) => api.post("/users", data),
	update: (id: string, d: object) => api.patch(`/users/${id}`, d),
	toggle: (id: string) => api.patch(`/users/${id}/toggle`),
	resetPin: (id: string, pin: string) =>
		api.post(`/users/${id}/reset-pin`, { pin }),
	delete: (id: string) => api.delete(`/users/${id}`),
};

export const categoriesAPI = {
	getAll: (active?: boolean) => api.get("/categories", { params: { active } }),
	create: (data: object) => api.post("/categories", data),
	update: (id: string, d: object) => api.patch(`/categories/${id}`, d),
	delete: (id: string) => api.delete(`/categories/${id}`),
	reorder: (ids: string[]) => api.post("/categories/reorder", { ids }),
};

export const menuAPI = {
	getAll: (params?: object) => api.get("/menu", { params }),
	getOne: (id: string) => api.get(`/menu/${id}`),
	create: (data: object) => api.post("/menu", data),
	update: (id: string, d: object) => api.patch(`/menu/${id}`, d),
	toggle: (id: string) => api.patch(`/menu/${id}/toggle`),
	delete: (id: string) => api.delete(`/menu/${id}`),
};

export const dealsAPI = {
	getAll: (active?: boolean) => api.get("/deals", { params: { active } }),
	validate: (code: string, subtotal: number) =>
		api.post("/deals/validate", { code, subtotal }),
	create: (data: object) => api.post("/deals", data),
	update: (id: string, d: object) => api.patch(`/deals/${id}`, d),
	toggle: (id: string) => api.patch(`/deals/${id}/toggle`),
	delete: (id: string) => api.delete(`/deals/${id}`),
};

export const tablesAPI = {
	getAll: (section?: string) => api.get("/tables", { params: { section } }),
	getOne: (id: string) => api.get(`/tables/${id}`),
	create: (data: object) => api.post("/tables", data),
	update: (id: string, d: object) => api.patch(`/tables/${id}`, d),
	setStatus: (id: string, d: object) => api.patch(`/tables/${id}/status`, d),
	delete: (id: string) => api.delete(`/tables/${id}`),
};

export const ordersAPI = {
	getAll: (params?: object) => api.get("/orders", { params }),
	getOne: (id: string) => api.get(`/orders/${id}`),
	create: (data: object) => api.post("/orders", data),
	update: (id: string, d: object) => api.patch(`/orders/${id}`, d),
	void: (id: string) => api.patch(`/orders/${id}/void`),
	receipt: (id: string) => api.get(`/orders/${id}/receipt`),
};

export const paymentsAPI = {
	getAll: (params?: object) => api.get("/payments", { params }),
	getMethods: () => api.get("/payments/methods"),
	getSummary: (date?: string) =>
		api.get("/payments/summary", { params: { date } }),
	process: (data: object) => api.post("/payments", data),
	refund: (id: string) => api.post(`/payments/${id}/refund`),
};

export const reportsAPI = {
	daily: (date?: string) => api.get("/reports/daily", { params: { date } }),
	weekly: () => api.get("/reports/weekly"),
	monthly: (month?: number, year?: number) =>
		api.get("/reports/monthly", { params: { month, year } }),
	items: () => api.get("/reports/items"),
	staff: () => api.get("/reports/staff"),
	financial: (params?: object) => api.get("/reports/financial", { params }),
	inventory: () => api.get("/reports/inventory"),
};

export const settingsAPI = {
	get: () => api.get("/settings"),
	update: (data: object) => api.patch("/settings", data),
};

export default api;
